/**
 * MultiAgentMetadataEditorPanel — COPIED from
 * pages/dev-agent/components/MetadataEditorPanel.tsx, made multi-agent-native.
 *
 * Swaps performed (per the shared contract):
 *   ./metadataEditor/useMetadataEditorPanel  -> ./useMetadataEditorPanel   (multi-agent store + branch save)
 *   ./metadataEditor/AcceptedCodeDiffSection -> ./AcceptedCodeDiffSection   (renders MultiAgentCompareView)
 *   ./metadataEditor/{Header,Shimmer,SaveErrorsBanner,AutoFixStatusBars,types} -> the local multi-agent copies
 *
 * Everything else (TabbedCodeEditor from @ncoder/code-editor, layout, props) is
 * IDENTICAL to the dev-agent panel so the MultiAgentIdePanel call site is a
 * one-line component swap. Save / Compare now run through the multi-agent path:
 *  - Compare resolves the original via metadata.source_artifact_id -> GET
 *    /api/artifacts/{id} (MultiAgentCompareView), with green/red per-hunk +
 *    create-new when no source artifact exists.
 */

import { TabbedCodeEditor } from "@ncoder/code-editor";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { formatValidationProgress } from "@/services/multi-agent";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import { AnalyzingShimmerBar } from "./AnalyzingShimmerBar";
import { SaveErrorsBanner } from "./SaveErrorsBanner";
import { AutoFixStatusBars } from "./AutoFixStatusBars";
import { MetadataEditorPanelHeader } from "./MetadataEditorPanelHeader";
import { AcceptedCodeDiffSection } from "./AcceptedCodeDiffSection";
import type { MetadataEditorPanelProps } from "./types";
import { useMetadataEditorPanel } from "./useMetadataEditorPanel";

export type { OpenFile, MetadataEditorPanelProps } from "./types";

export function MultiAgentMetadataEditorPanel(props: MetadataEditorPanelProps) {
  const {
    panelWidth,
    activeFileId,
    extraActions,
    isAnalyzing,
    onRunTest,
    acceptedCode,
    onLoadIntoChat,
    onAcceptedCodeChange,
    isBusy,
    isTestRunning,
    onReorderTabs,
    onPromoteTab,
  } = props;

  const vm = useMetadataEditorPanel(props);

  // Server-side compile-gate progress (`validate_progress` SSE) — rendered by
  // the banner only while THIS panel's save is in flight (the save flow clears
  // the slot at start, so a previous save's last event can never leak in).
  const validationProgress = useMultiAgentStore((s) => s.validationProgress);

  if (vm.shouldRenderNull) return null;

  const loadIntoChatHandler =
    onLoadIntoChat &&
    vm.activeFile &&
    vm.activeFile.name !== "rules.cls"
      ? () => {
          const f = vm.activeFile;
          if (!f) return;
          onLoadIntoChat({
            id: f.id,
            name: f.name,
            metadataType: f.metadataType,
            content:
              vm.contentByFileId[f.id] ??
              vm.metadataFileData?.content ??
              vm.originalByFileId[f.id] ??
              "",
          });
        }
      : undefined;

  return (
    <div
      className="flex h-full min-h-0 shrink-0 flex-col overflow-hidden"
      style={{ width: panelWidth }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card"
        data-resizable-editor
      >
        <div className="relative flex h-full min-h-0 flex-col bg-background">
          <MetadataEditorPanelHeader
            isLoadingMetadataFile={vm.isLoadingMetadataFile}
            activeFile={vm.activeFile}
            activeBadge={vm.activeBadge}
            extraActions={extraActions}
            onRunTest={onRunTest}
            activeFileId={activeFileId}
            editorContent={vm.contentByFileId[activeFileId ?? ""] ?? ""}
            isBusy={isBusy}
            isTestRunning={isTestRunning}
            onClose={vm.handleClose}
          />

          {/* Compare toggle removed per request — the accepted-code diff still
              opens via the chat/workspace Compare action when applicable. */}

          <AnalyzingShimmerBar visible={vm.isSaving || !!isAnalyzing} />

          {vm.saveErrors && (
            <SaveErrorsBanner
              saveErrors={vm.saveErrors}
              isSaving={vm.isSaving}
              onDismiss={() => vm.setSaveErrors(null)}
              onStop={vm.abortSave}
              liveProgress={
                vm.isSaving && validationProgress
                  ? formatValidationProgress(validationProgress)
                  : null
              }
            />
          )}

          <AutoFixStatusBars
            autoFixState={vm.autoFixState}
            errorExpanded={vm.errorExpanded}
            setErrorExpanded={vm.setErrorExpanded}
            onCancelAutoFix={vm.cancelAutoFix}
            onResetAutoFix={vm.resetAutoFix}
          />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {vm.metadataFileError &&
              activeFileId &&
              !vm.isVirtualFile &&
              vm.contentByFileId[activeFileId] === undefined && (
                <div className="flex flex-1 items-center justify-center p-4 text-center">
                  <div>
                    <p className="text-sm text-ma-err-text">Failed to load file</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {vm.metadataFileError.message}
                    </p>
                  </div>
                </div>
              )}

            {vm.showDiff && activeFileId && acceptedCode && (
              <AcceptedCodeDiffSection
                activeFileId={activeFileId}
                activeFile={vm.activeFile}
                acceptedCode={acceptedCode}
                originalContentForDiff={vm.originalContentForDiff}
                originalArtifactId={vm.sourceArtifactId}
                editorTheme={vm.editorTheme}
                diffEditorRef={vm.diffEditorRef}
                diffOverlayOpen={vm.diffOverlayOpen}
                setDiffOverlayOpen={vm.setDiffOverlayOpen}
                diffHunkTotal={vm.diffHunkTotal}
                diffHunkResolved={vm.diffHunkResolved}
                setDiffHunkTotal={vm.setDiffHunkTotal}
                setDiffHunkResolved={vm.setDiffHunkResolved}
                setContentByFileId={vm.setContentByFileId}
                setOriginalByFileId={vm.setOriginalByFileId}
                setDirtyByFileId={vm.setDirtyByFileId}
                onAcceptedCodeChange={onAcceptedCodeChange}
                handleSave={vm.handleSave}
              />
            )}

            {vm.showTabbedEditor && (
              <>
                {vm.isLoadingMetadataFile &&
                vm.packageTabs.every(
                  (t) => t.value === "" && t.id === activeFileId,
                ) ? (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <p className="text-xs text-muted-foreground">Loading file...</p>
                    </div>
                  </div>
                ) : (
                  <TabbedCodeEditor
                    tabs={vm.packageTabs}
                    activeTabId={activeFileId}
                    onTabChange={vm.handleTabChange}
                    onTabClose={vm.handleTabClose}
                    // Double-click a preview tab to keep it, as in the Metadata
                    // Explorer and in VS Code.
                    onTabDoubleClick={onPromoteTab}
                    onTabReorder={onReorderTabs}
                    onContentChange={vm.handleContentChange}
                    theme={vm.editorTheme}
                    formatApi={vm.formatApi}
                    lintApi={vm.lintApi}
                    onSave={vm.showDiff ? undefined : vm.handleSave}
                    onAbandon={vm.activeBranch ? vm.handleAbandon : undefined}
                    onToggleViewMain={
                      vm.activeBranch ? vm.handleToggleViewMain : undefined
                    }
                    onToggleViewGithub={vm.isGithubConnected ? vm.handleToggleViewGithub : undefined}
                    branchName={vm.activeBranch?.name}
                    viewingMain={vm.viewingMain}
                    viewingGithub={vm.viewingGithub}
                    githubContent={vm.githubNotFound ? null : (vm.githubContent ?? null)}
                    isLoadingGithub={vm.isLoadingGithub}
                    isAbandoning={vm.isAbandoning}
                    onLoadIntoChat={loadIntoChatHandler}
                    externalMarkers={
                      vm.isVirtualFile || vm.metadataFileError
                        ? []
                        : [...(vm.analysisMarkers || []), ...vm.coverageMarkers]
                    }
                    coverageLines={
                      vm.covData &&
                      vm.activeFile &&
                      vm.covData.className === bareClassName(vm.activeFile.name)
                        ? vm.covData
                        : undefined
                    }
                    isLoadingContent={
                      vm.isLoadingMetadataFile &&
                      vm.contentByFileId[activeFileId ?? ""] === undefined
                    }
                    isDirty={!!vm.isDirty}
                    isSaving={vm.isSaving || vm.isBranchSaving}
                    editorOptions={vm.editorOptions}
                    className="min-h-0 flex-1"
                  />
                )}
              </>
            )}
          </div>

          {/* GitHub no-repo overlay */}
          {vm.githubNoRepo && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm gap-3">
              <div className="flex flex-col items-center gap-2 text-center max-w-sm">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">No GitHub repository selected</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Go to the <strong className="text-foreground">Git module</strong> and select a repository first, then come back to view this file on GitHub.
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Link
                    to="/studio/git"
                    className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    Go to Git module
                  </Link>
                  <button
                    type="button"
                    onClick={vm.handleToggleViewGithub}
                    className="px-3 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                  >
                    Back to Local
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* GitHub not-found overlay */}
          {vm.githubNotFound && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm gap-3">
              <div className="flex flex-col items-center gap-3 text-center max-w-xs">
                <div className="h-10 w-10 rounded-full bg-ma-warn-soft flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-ma-warn-text" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Oops, file not found</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                    <span className="font-mono text-foreground">{vm.activeFile?.name}</span> isn't in{" "}
                    <span className="font-mono text-foreground">{vm.selectedGitRepo?.full_name}</span>{" "}
                    on <span className="font-mono text-foreground">{vm.gitRef}</span>.
                    Try switching repo or branch, or push this file to GitHub first.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={vm.handleToggleViewGithub}
                  className="px-3 py-1.5 text-xs rounded-md bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                >
                  Back to Local
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
